import classnames from 'classnames';
import React, { ReactNode } from 'react';
import AnimateHeight from 'react-animate-height';

import { ButtonTypes, IconButton } from '@eika/button';
import '@eika/expand-card-style';
import { IconTypes } from '@eika/icon';
import { Label } from '@eika/label';

type H3Props = {
    children: ReactNode;
    className?: string;
};
const H3 = ({ children, className }: H3Props) => {
    return <h3 className={className}>{children}</h3>;
};
export interface ExpandCardProps {
    className?: string;
    icon?: IconTypes;
    iconExpanded?: IconTypes;
    title: string;
    subtitle?: ReactNode;
    extraSubtitle?: string;
    label?: ReturnType<typeof Label>;
    showLabelWhenExpanded?: boolean;
    children: ReactNode;
    id: string;
    customHeaderLevelElement?: () => JSX.Element;
    isExpanded: boolean;
    setIsExpanded: (isExpanded: boolean) => void;
    dataTrackId: string;
    dataTestId?: string;
}
const ExpandCard = ({
    className,
    icon = IconTypes.LITEN_PIL_NED,
    iconExpanded = IconTypes.LITEN_PIL_OPP,
    children,
    title,
    subtitle,
    extraSubtitle,
    label,
    showLabelWhenExpanded = false,
    id,
    customHeaderLevelElement,
    isExpanded,
    setIsExpanded,
    dataTrackId = title,
    dataTestId,
}: ExpandCardProps): JSX.Element => {
    const showLabel = showLabelWhenExpanded ? !!label : !isExpanded && !!label;
    const Header = customHeaderLevelElement || H3;
    const readerText = isExpanded ? 'lukk' : 'åpne';
    return (
        <section
            className={classnames('sgw-expand-card', className, { expanded: isExpanded })}
            aria-live="polite"
            aria-labelledby={`expand-card-${id}`}
            data-testid={dataTestId}
        >
            <div className="sgw-expand-card__header">
                <div className="sgw-expand-card__header-content">
                    <Header className="sgw-expand-card__title-row">
                        {extraSubtitle && <strong className="sgw-expand-card__extra-subtitle">{extraSubtitle}</strong>}
                        <span id={`expand-card-${id}`} className="sgw-expand-card__title">
                            {title}
                        </span>
                    </Header>
                    {subtitle && <p className="sgw-expand-card__subtitle">{subtitle}</p>}
                    {showLabel && <div className="sgw-expand-card__label">{label}</div>}
                </div>
                <IconButton
                    className="sgw-expand-card__button"
                    type="button"
                    buttonType={ButtonTypes.ICON_NEGATIVE}
                    onClick={() => setIsExpanded(!isExpanded)}
                    aria-expanded={isExpanded}
                    icon={isExpanded ? iconExpanded : icon}
                    screenReaderText={readerText}
                    data-track-id={dataTrackId}
                    data-track-linkname={readerText}
                />
            </div>
            <AnimateHeight height={isExpanded ? 'auto' : 0} contentClassName="sgw-expand-card__content">
                {isExpanded && children}
            </AnimateHeight>
        </section>
    );
};

export default ExpandCard;
